import type { Round } from '../types/index.js';
import { BAGS_PER_PLAYER, WIN_SCORE } from '../constants/index.js';

export interface Standings {
  team1Score: number;
  team2Score: number;
}

/**
 * Computes accumulated cancellation scores from all rounds.
 * Each round contributes the NET points (|team1 - team2|) to the winning team.
 *
 * @example
 *   Round: team1=12, team2=8 → net 4 → team1 gets 4 points
 */
export function computeStandings(rounds: Round[]): Standings {
  let team1Score = 0;
  let team2Score = 0;

  for (const round of rounds) {
    const net = round.team1Score - round.team2Score;
    if (net > 0) {
      team1Score += net;
    } else if (net < 0) {
      team2Score += Math.abs(net);
    }
    // Tied round → no points awarded (standard cancellation)
  }

  return { team1Score, team2Score };
}

/**
 * Returns the net points for a single round.
 * Positive = team1 wins the round, negative = team2, zero = tie.
 */
export function computeRoundNet(team1RoundScore: number, team2RoundScore: number): number {
  return team1RoundScore - team2RoundScore;
}

/**
 * Determines if a game has been won based on current standings.
 * Returns the winning team ID ('team1' | 'team2') or null if game is ongoing.
 */
export function determineWinner(standings: Standings): 'team1' | 'team2' | null {
  if (standings.team1Score >= WIN_SCORE) return 'team1';
  if (standings.team2Score >= WIN_SCORE) return 'team2';
  return null;
}

/**
 * Validates that a player's bag counts sum to exactly BAGS_PER_PLAYER.
 * Used in Intermediate PPR entry to enable the Submit button.
 */
export function validateBagCount(bagsIn: number, bagsOn: number, bagsOff: number): boolean {
  return bagsIn + bagsOn + bagsOff === BAGS_PER_PLAYER;
}

/**
 * Computes points from bag counts:
 *   bagsIn * 3 + bagsOn * 1
 */
export function computePlayerPoints(bagsIn: number, bagsOn: number): number {
  return bagsIn * 3 + bagsOn * 1;
}

/**
 * Skins game: computes rolled-over points and round wins.
 * Returns the running totals after processing all rounds.
 */
export function computeSkinsStandings(
  rounds: Round[],
  pointsPerWin: number,
): { team1Wins: number; team2Wins: number; team1Points: number; team2Points: number } {
  let team1Wins = 0;
  let team2Wins = 0;
  let team1Points = 0;
  let team2Points = 0;
  let rolledOver = 0;

  for (const round of rounds) {
    const net = round.team1Score - round.team2Score;
    const available = pointsPerWin + rolledOver;

    if (net > 0) {
      team1Wins += 1;
      team1Points += available;
      rolledOver = 0;
    } else if (net < 0) {
      team2Wins += 1;
      team2Points += available;
      rolledOver = 0;
    } else {
      // Tie — roll over
      rolledOver += pointsPerWin;
    }
  }

  return { team1Wins, team2Wins, team1Points, team2Points };
}
